<template>
  <section class="bcf" data-testid="vigilance-carte">
    <h4 class="bcf-title">
      <i class="fas fa-map-location-dot" aria-hidden="true"></i>
      Formateurs autour du lieu de formation
    </h4>

    <InlineNotice v-if="etat === 'sans-lieu'" variant="info" data-testid="vigilance-carte-sans-lieu">
      Aucun lieu connu pour cette demande : renseignez le lieu de la formation pour voir les formateurs proches.
    </InlineNotice>

    <InlineNotice v-else-if="etat === 'introuvable'" variant="warning" data-testid="vigilance-carte-introuvable">
      Le lieu « {{ lieu?.libelle }} » n’a pas pu être placé sur la carte : vérifiez l’adresse de la formation.
      <Button variant="ghost" size="sm" icon="fas fa-redo" @click="preparer()">Réessayer</Button>
    </InlineNotice>

    <template v-else>
      <p class="bcf-criteres" data-testid="vigilance-carte-criteres">
        <span>Autour de <strong>{{ lieu?.libelle }}</strong></span>
        <span v-if="critere">{{ critere }}</span>
        <span v-else-if="etat === 'prete'">Aucune compétence renseignée pour cette formation au catalogue : tous les formateurs sont montrés</span>
        <span>Statut, type et réseau : tous</span>
      </p>

      <div class="bcf-rayon">
        <span class="bcf-rayon-label">Distance max. — {{ filters.rayonKm }} km</span>
        <Slider
          :model-value="filters.rayonKm"
          :min="RAYON_MIN_KM"
          :max="RAYON_MAX_KM"
          :step="RAYON_PAS_KM"
          :disabled="etat !== 'prete'"
          :format="(v) => `${v} km`"
          aria-label="Distance maximale autour du lieu de formation"
          test-id="vigilance-carte-rayon"
          @update:model-value="setRayon"
        />
      </div>

      <p v-if="resume" class="bcf-resume" data-testid="vigilance-carte-resume">{{ resume }}</p>

      <InlineNotice v-if="error" variant="danger" data-testid="vigilance-carte-erreur">
        La carte n’a pas pu être chargée : {{ error }}
        <Button variant="ghost" size="sm" icon="fas fa-redo" @click="charger()">Réessayer</Button>
      </InlineNotice>

      <div v-else class="bcf-carte">
        <div v-if="etat === 'preparation' || (loading && !formateurs.length)" class="bcf-attente">
          <LoadingSpinner />
        </div>
        <FormateursMap
          v-else
          :formateurs="formateurs"
          :centre="filters.point"
          :rayon-km="filters.rayonKm"
          :highlighted-id="surligne"
          @highlight="surligne = $event"
        />
      </div>
    </template>
  </section>
</template>

<script setup>
/**
 * LA CARTE DES FORMATEURS, dans le Point de vigilance de l'analyse du besoin :
 * la carte de la liste des formateurs — mêmes points, même bulle, même lien
 * vers la fiche —, remplie d'office pour la demande. Seule la distance se
 * règle (cf. `useBesoinCarteFormateurs`).
 *
 * Elle se prépare à son affichage : le Point de vigilance n'est monté qu'une
 * fois ouvert, et le lieu a pu changer dans une section précédente.
 */
import { onMounted, ref, toRef } from 'vue'
import { Button, InlineNotice, LoadingSpinner, Slider } from '../ui'
import FormateursMap from '../formateurs/FormateursMap.vue'
import { useBesoinCarteFormateurs } from '../../composables/quotes/useBesoinCarteFormateurs.js'
import { RAYON_MAX_KM, RAYON_MIN_KM, RAYON_PAS_KM } from '../../constants/formateurCiblage.js'

const props = defineProps({
  /** La demande telle qu'affichée (snake_case). */
  quote: { type: Object, default: null },
  /** La formation du catalogue retenue sur la demande. */
  selectedFormation: { type: Object, default: null }
})

const {
  formateurs, loading, error, charger, filters, lieu, etat, critere, resume, preparer, setRayon
} = useBesoinCarteFormateurs(toRef(props, 'quote'), toRef(props, 'selectedFormation'))

const surligne = ref(null)


onMounted(preparer)
</script>

<style scoped>
.bcf {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.bcf-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.bcf-criteres {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-1) var(--spacing-3);
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.bcf-criteres span + span::before {
  content: '·';
  margin-right: var(--spacing-3);
}

.bcf-rayon {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  max-width: 320px;
}

.bcf-rayon-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.bcf-resume {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.bcf-carte {
  position: relative;
  height: 420px;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.bcf-attente {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
